
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { TeenOfTheDaySubmission } from '@/types/teen-of-the-day';
import { Check, X, Eye, Calendar, Clock, Star } from 'lucide-react';

const TeenOfTheDayManagement = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<TeenOfTheDaySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<TeenOfTheDaySubmission | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('teen_of_the_day_submissions')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) {
        throw error;
      }

      if (data) {
        // Fetch profiles for each submission
        const submissionsWithProfiles = await Promise.all(
          data.map(async (submission) => {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('full_name, avatar_url, first_name, last_name')
              .eq('id', submission.user_id)
              .maybeSingle();

            return {
              ...submission,
              status: submission.status as 'pending' | 'approved' | 'rejected',
              user_name: profileData?.full_name || 
                         `${profileData?.first_name || ''} ${profileData?.last_name || ''}`.trim() || 
                         'Anonymous Teen',
              user_avatar: profileData?.avatar_url
            };
          })
        );

        setSubmissions(submissionsWithProfiles);
      }
    } catch (error: any) {
      console.error('Error fetching submissions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch submissions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (id: string, status: 'approved' | 'rejected', featuredDate?: string) => {
    try {
      const updates: any = {
        status,
        reviewed_at: new Date().toISOString()
      };

      if (status === 'approved' && featuredDate) {
        updates.featured_date = featuredDate;
      }

      const { error } = await supabase
        .from('teen_of_the_day_submissions')
        .update(updates)
        .eq('id', id);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: `Submission ${status} successfully`
      });

      fetchSubmissions();
      setReviewDialogOpen(false);
      setSelectedSubmission(null);
    } catch (error: any) {
      console.error('Error updating submission:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update submission",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="border-yellow-300 text-yellow-700 bg-yellow-50">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Teen of the Day Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading submissions...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 text-yellow-500" />
            <div>
              <CardTitle>Teen of the Day Submissions</CardTitle>
              <CardDescription>
                Review and manage teen feature requests ({submissions.length} total)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {submissions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Star className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No submissions yet</p>
              <p className="text-sm">New submissions will appear here for review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <Card key={submission.id} className="border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={submission.user_avatar} alt={submission.user_name} />
                        <AvatarFallback>
                          {submission.user_name?.split(' ').map(n => n[0]).join('') || 'T'}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{submission.user_name}</h3>
                            <p className="text-blue-600 font-medium">{submission.title}</p>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {submission.story}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(submission.submitted_at).toLocaleDateString()}
                              </div>
                              {submission.featured_date && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Featured: {new Date(submission.featured_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getStatusBadge(submission.status)}
                          </div>
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setReviewDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                          
                          {submission.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => updateSubmissionStatus(submission.id, 'approved', new Date().toISOString().split('T')[0])}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                onClick={() => updateSubmissionStatus(submission.id, 'rejected')}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ReviewSubmissionDialog
        submission={selectedSubmission}
        open={reviewDialogOpen}
        onOpenChange={setReviewDialogOpen}
        onStatusUpdate={updateSubmissionStatus}
      />
    </>
  );
};

interface ReviewSubmissionDialogProps {
  submission: TeenOfTheDaySubmission | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusUpdate: (id: string, status: 'approved' | 'rejected', featuredDate?: string) => void;
}

const ReviewSubmissionDialog = ({ submission, open, onOpenChange, onStatusUpdate }: ReviewSubmissionDialogProps) => {
  const [featuredDate, setFeaturedDate] = useState(new Date().toISOString().split('T')[0]);

  if (!submission) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={submission.user_avatar} alt={submission.user_name} />
              <AvatarFallback>
                {submission.user_name?.split(' ').map(n => n[0]).join('') || 'T'}
              </AvatarFallback>
            </Avatar>
            Review Submission - {submission.user_name}
          </DialogTitle>
          <DialogDescription>
            Submitted on {new Date(submission.submitted_at).toLocaleDateString()} • Status: {submission.status}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          <div>
            <h3 className="font-semibold text-lg mb-2">Title</h3>
            <p className="text-blue-600 font-medium">{submission.title}</p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Story</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="whitespace-pre-wrap">{submission.story}</p>
            </div>
          </div>

          {submission.achievements && (
            <div>
              <h3 className="font-semibold text-lg mb-2">Achievements</h3>
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="whitespace-pre-wrap">{submission.achievements}</p>
              </div>
            </div>
          )}

          {submission.image_url && (
            <div>
              <h3 className="font-semibold text-lg mb-2">Photo</h3>
              <div className="relative h-64 rounded-lg overflow-hidden">
                <img
                  src={submission.image_url}
                  alt={submission.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {submission.status === 'pending' && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Featured Date (if approving)
                </label>
                <Input
                  type="date"
                  value={featuredDate}
                  onChange={(e) => setFeaturedDate(e.target.value)}
                  className="w-auto"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => onStatusUpdate(submission.id, 'approved', featuredDate)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve & Feature
                </Button>
                <Button
                  onClick={() => onStatusUpdate(submission.id, 'rejected')}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeenOfTheDayManagement;
